import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { adaptTaskSummaryDto } from '../adapters';
import { serializeFilters, serializeSort } from '../utils/filter-serializer';
import { TASK_KEYS } from './queryKeys';
import { TASK_COLLECTION_STALE_TIME } from './taskQueryConfig';
import { seedTaskEntities } from './useTaskEntityCache';
import type {
  GroupByOption,
  Task,
  TaskApiParams,
  TaskFilters,
  TaskListSortBy,
  TaskQueryResult,
} from '../types';

const PAGE_SIZE = 50;

interface UseTasksInfiniteParams {
  projectId: string;
  boardId?: string;
  groupBy: GroupByOption;
  groupValue: string;
  sortBy?: TaskListSortBy;
  /** Kept for interface compatibility — server-side showCompleted pending backend Field Registry */
  showCompleted: boolean;
  includeArchived?: boolean;
  filters: TaskFilters;
}

interface TasksInfinitePage {
  items: Task[];
  total: number;
  nextCursor: string | null;
}

/**
 * Fetches a project's task list with cursor pagination, filters, sorting, and optional grouping.
 *
 * @returns Infinite query result; `data.pages` each contain a flat `items` array plus `nextCursor`.
 *
 * @remarks
 * - When `groupBy` is `'none'`, sends `view=list` for a flat response. When grouped, sends
 *   `groupBy` + `groupKey` + `topLevelOnly: true` and reads items from `groups[0]` — one call
 *   per group/column. `topLevelOnly` keeps subtasks out of the group's top-level items (List
 *   view nests them under their parent row separately) — without it, a grouped List request
 *   is indistinguishable server-side from a Kanban column request, which wants subtasks
 *   flattened as their own cards.
 * - `showCompleted` is accepted for interface compatibility but not forwarded to the API.
 *   Server-side `isCompleted` filtering is pending backend Field Registry support.
 * - Disabled automatically when `activeOrganizationId` is not set (org switching in progress).
 */
export function useTasksInfinite({
  projectId,
  boardId,
  groupBy,
  groupValue,
  sortBy,
  includeArchived,
  filters,
}: UseTasksInfiniteParams) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);
  const queryClient = useQueryClient();

  const serializedFilters = serializeFilters(filters);
  const serializedSort = sortBy ? serializeSort(sortBy) : undefined;

  const isGrouped = groupBy !== 'none';

  const apiParams: TaskApiParams = {
    limit: PAGE_SIZE,
    // view=list forces flat response and ignores groupBy — omit it when grouping is active
    ...(!isGrouped && { view: 'list' }),
    ...(isGrouped && { groupBy }),
    // Always send groupKey when grouped — empty string is a valid key (e.g. unassigned, no-group)
    ...(isGrouped && { groupKey: groupValue }),
    // Without this, a grouped List request is indistinguishable server-side from a
    // Kanban column request, and subtasks come back flattened as top-level items
    // alongside their own nested rendering under the parent row.
    ...(isGrouped && { topLevelOnly: true }),
    ...(serializedFilters && { filters: serializedFilters }),
    ...(serializedSort && { sort: serializedSort }),
    ...(filters.search && { search: filters.search }),
    ...(includeArchived && { includeArchived }),
    ...(boardId && { boardId }),
  };

  const queryKey = TASK_KEYS.query(orgId ?? '', projectId, apiParams);

  const query = useInfiniteQuery<TasksInfinitePage>({
    queryKey,
    queryFn: async ({ pageParam, signal }): Promise<TasksInfinitePage> => {
      const result: TaskQueryResult = await tasksService.getQuery(
        projectId,
        {
          ...apiParams,
          cursor: pageParam as string | undefined,
        },
        signal,
      );
      // Grouped response: items live in groups[0]; flat response: items at root
      const group = isGrouped ? result.groups[0] : undefined;
      const rawItems = group ? group.items : result.items;
      const nextCursor = group ? group.nextCursor : result.nextCursor;
      const total = group ? group.count : result.total;
      const tasks = rawItems.map(adaptTaskSummaryDto);
      seedTaskEntities(queryClient, orgId ?? '', tasks);
      return { items: tasks, total, nextCursor };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: TASK_COLLECTION_STALE_TIME,
    enabled: !!orgId && !!projectId,
  });

  return { ...query, queryKey };
}
